import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const axiosInstance = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL || "https://admin-api.cms.chargingadda.com",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;

export const clearAuthAndRedirect = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  
  console.error("🚫 Clearing auth data and redirecting to login...");
  
  // Clear all auth-related data
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("user_info");
  localStorage.removeItem("current_org_subdomain");
  localStorage.removeItem("current_org_id");
  localStorage.removeItem("user_org_subdomain");
  localStorage.removeItem("user_org_id");
  localStorage.removeItem("code_verifier");
  localStorage.removeItem("original_subdomain");
  
  if (window.location.pathname !== "/login" && window.location.pathname !== "/callback") {
    console.log("🔄 Redirecting to /login");
    window.location.href = "/login";
  }
  
  setTimeout(() => { isRedirecting = false; }, 2000);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    console.log("🔑 Token from localStorage:", token ? "Present" : "Missing");

    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
      console.log("✅ Authorization header applied");
    } else {
      console.warn("⚠️ No token found in localStorage");
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Interceptor Error:", error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    console.error("❌ Response Interceptor Error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      hasResponse: !!error.response,
      request: error.request?.status,
    });
    
    // Method 1: Check response status (when CORS allows)
    if (error.response?.status === 401) {
      console.error("🚫 401 Unauthorized (from response)");
      clearAuthAndRedirect();
      return Promise.reject(new Error("Session expired"));
    }
    
    // Method 2: Check request status (sometimes available even when response is blocked)
    if (error.request?.status === 401) {
      console.error("🚫 401 Unauthorized (from request)");
      clearAuthAndRedirect();
      return Promise.reject(new Error("Session expired"));
    }
    
    // Method 3: Check if error message contains "401"
    if (error.message?.includes("401") || error.message?.includes("Unauthorized")) {
      console.error("🚫 401 Unauthorized (from message)");
      clearAuthAndRedirect();
      return Promise.reject(new Error("Session expired"));
    }

    // Method 4: Network error with token (likely CORS-blocked 401 — the API
    // does not send Access-Control-Allow-Origin on error responses, so the
    // browser hides the 401 status from JS and axios reports ERR_NETWORK).
    if (!error.response && error.code === 'ERR_NETWORK') {
      const token = localStorage.getItem("access_token");

      if (token) {
        console.error("🚫 Network error with token → Assuming CORS-blocked 401");
        clearAuthAndRedirect();
        return Promise.reject(new Error("Session expired"));
      } else {
        console.error("🌐 Network error without token");
      }
    }

    // Other errors
    if (error.response) {
      console.error(`⚠️ HTTP Error ${error.response.status}`);
    } else {
      console.error("🌐 Network error - no response received");
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;